import type { PasswordChangeRequest, Profile, ProfilePatch } from '@liniya/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { queryKeys } from '../query-keys';
import { notify } from '../store/ui';

export function useProfile() {
  return useQuery({
    queryKey: queryKeys.profile,
    queryFn: () => api.profile.get(),
    staleTime: 5 * 60_000,
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: ProfilePatch) => api.profile.update(patch),
    onSuccess: (profile) => {
      queryClient.setQueryData<Profile>(queryKeys.profile, profile);
      notify('Профиль сохранён');
    },
  });
}

export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => api.profile.uploadAvatar(file, file.name),
    onSuccess: (profile) => {
      queryClient.setQueryData<Profile>(queryKeys.profile, profile);
      notify('Фотография обновлена');
    },
    onError: () => notify('Не удалось загрузить фотографию.', 'error'),
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PasswordChangeRequest) => api.auth.changePassword(request),
    onSuccess: async () => {
      // The server ends every other sign-in as part of the change, so the
      // device list on screen is already out of date.
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      notify('Пароль изменён. Другие устройства отключены.');
    },
  });
}

export function useDevices() {
  return useQuery({
    queryKey: queryKeys.devices,
    queryFn: () => api.devices.list(),
  });
}

export function useRevokeDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ deviceId, password }: { deviceId: string; password: string }) =>
      api.devices.revoke(deviceId, password),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      notify('Устройство отозвано');
    },
  });
}

export function useLogoutOtherDevices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (password: string) => api.devices.logoutOthers(password),
    onSuccess: async () => {
      // Signing out elsewhere also ends the token chains of those devices, so
      // the list has to be reloaded rather than patched.
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices });
      notify('Другие устройства отключены');
    },
  });
}
